import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, ComboBox, Loading } from "@carbon/react";
import ConfirmDialog from "../../carbon/ConfirmDialog/ConfirmDialog";
import {
  MaterialOfferResponse,
  MaterialResponse,
  UserCompanyDetail,
} from "../../../types";
import { useUsersByCompany } from "../../../hooks/useUsers";
import {
  useDelegateAllMaterials,
  useUpdateMaterial,
} from "../../../hooks/useMaterials";
import { useUpdateMaterialOffer } from "../../../hooks/useMaterialsOffers";
import { useNotification, useUser } from "../../../context";
import "./delegate.scss";

type DelegateProps = {
  material?: MaterialResponse;
  materialOffer?: MaterialOfferResponse;
  onCloseDelegate?: () => void;
  isDelegateAll?: boolean;
  isDelegateMaterialOffer?: boolean;
  /**
   * When provided, `Delegate` renders itself as a self-contained Carbon `ConfirmDialog`
   * (`ComposedModal` under the hood), gated on this flag — the single-material delegate action on
   * `components/Material/Material.tsx` (Task 8's own scope: "the single-material Delegate action
   * reassigns the material's `owner`") is the only caller that passes it.
   *
   * The other two callers keep `open` undefined on purpose: `components/Offer/IsMaterialOffer.tsx`
   * (the material-OFFER delegate — an Offer-owned component, explicitly out of this task's scope
   * fence: "do NOT touch ... any bidding/offer components") and
   * `containers/Delegate/Delegate.tsx` ("delegate all my materials" — not one of this task's four
   * named files, and not covered by any e2e safety net). Both keep embedding this component's
   * plain content inside their own pre-existing `Slide`/bare-`div` wrapper exactly as before —
   * leaving `open` undefined is what keeps their behaviour unchanged without editing either file.
   */
  open?: boolean;
};

/**
 * The delegate-to-a-colleague dialog, shared by three flows (see `open` above). Rebuilt on Carbon
 * (Task 8): a `ComboBox` colleague picker replaces the pre-Carbon `PickList` (`react-select`), and
 * the single-material flow's own chrome is a real `ConfirmDialog`/`ComposedModal` rather than the
 * plain `<div>` `Slide` used to wrap. The picker's selected value is the `UserCompanyDetail`
 * object itself (not a `{value, label}` wrapper the way `react-select`'s `PickList` shaped it) —
 * `onDelegateMaterial` below still sends exactly `owner: { id: selected.id }` on
 * `PUT /material/{id}` (`useUpdateMaterial`/`onEditMaterial`), the contract the backend fix on
 * this branch (commit d908e909) restored company-scoped owner reassignment for. Nothing about
 * that request contract changes here — only the UI that builds it.
 */
const Delegate = ({
  material,
  materialOffer,
  onCloseDelegate,
  isDelegateAll = false,
  isDelegateMaterialOffer = false,
  open,
}: DelegateProps) => {
  const { user } = useUser();
  const { openNotification } = useNotification();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<UserCompanyDetail | null>(null);

  const usersByCompany = useUsersByCompany(user?.id || "");

  const { mutate: mutateMaterial, isPending: isDelegatingMaterial } =
    useUpdateMaterial(
      () => {
        openNotification(`${t("delegate.success")} ${selected?.fullName}!`);
        return navigate(`/materials`);
      },
      () => openNotification(t("newMaterialForm.error"))
    );

  const { mutate: mutateMaterialOffer, isPending: isDelegatingMaterialOffer } =
    useUpdateMaterialOffer(
      () => {
        openNotification(
          `${t("delegate.successMaterialOffer")} ${selected?.fullName}!`
        );
        return navigate(`/material/offers`);
      },
      () => openNotification(t("newMaterialForm.error"))
    );

  const { mutate: mutateAllMaterials, isPending: isDelegatingAll } =
    useDelegateAllMaterials(
      () => {
        openNotification(`${t("delegate.all")} ${selected?.fullName}!`);
        return navigate(`/materials`);
      },
      () => openNotification(t("newMaterialForm.error"))
    );

  const isPending =
    isDelegatingMaterialOffer || isDelegatingMaterial || isDelegatingAll;

  const onDelegateMaterial = () => {
    if (!selected?.id) return;
    if (isDelegateMaterialOffer && materialOffer?.id) {
      mutateMaterialOffer({
        materialOfferId: materialOffer.id,
        body: {
          ...materialOffer,
          user: { id: selected.id },
        },
      });
    } else if (isDelegateAll) {
      mutateAllMaterials(selected.id);
    } else if (material) {
      mutateMaterial({
        materialId: material.id,
        body: {
          ...material,
          user,
          owner: { id: selected.id },
        },
      });
    }
  };

  const picker = (
    <ComboBox<UserCompanyDetail>
      id="delegate-user-picker"
      items={usersByCompany.data ?? []}
      itemToString={(item) => item?.fullName ?? ""}
      selectedItem={selected}
      onChange={({ selectedItem }) => setSelected(selectedItem ?? null)}
      placeholder={t("delegate.select")}
      aria-label={t("delegate.delegateTo")}
      shouldFilterItem={({ item, inputValue }) =>
        !inputValue ||
        (item?.fullName ?? "").toLowerCase().includes(inputValue.toLowerCase())
      }
      disabled={isPending}
    />
  );

  if (open !== undefined) {
    return (
      <ConfirmDialog
        open={open}
        title={t("delegate.delegateTo")}
        body={picker}
        confirmLabel={t("delegate.delegate")}
        onConfirm={onDelegateMaterial}
        onCancel={() => onCloseDelegate?.()}
        isPending={isPending}
      />
    );
  }

  return (
    <div className="material-delegate">
      {isPending ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <>
          <p className="material-delegate__label">{t("delegate.delegateTo")}</p>
          {picker}
          <div className="material-delegate__actions">
            <Button kind="secondary" onClick={onCloseDelegate}>
              {t("cta.cancel")}
            </Button>
            <Button onClick={onDelegateMaterial}>
              {t("delegate.delegate")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Delegate;
